import { IInitAction } from './init'
import { IEditionMetadata, PackType } from './pack'
import { IPollingTaskData } from './polling'

// Action types
export enum types {
  READ_LOCAL_FILE = 'READ_LOCAL_FILE',
  READ_LOCAL_FILE__VALIDATE_FAILURE = 'READ_LOCAL_FILE__VALIDATE_FAILURE',
  READ_LOCAL_FILE__CANCEL = 'READ_LOCAL_FILE__CANCEL',
  CLOSE_UPLOAD_DIALOG = 'CLOSE_UPLOAD_DIALOG',
  UPLOAD__STARTED = 'UPLOAD__STARTED',
  UPLOAD__RETRIEVED_TMP_LINK = 'UPLOAD__RETRIEVED_TMP_LINK',
  UPLOAD__PROGRESS = 'UPLOAD__PROGRESS',
  UPLOAD__SUCCESS = 'UPLOAD__SUCCESS',
  UPLOAD__FAILURE = 'UPLOAD__FAILURE',
  UPLOAD__CANCELED = 'UPLOAD__CANCELED',
  REOPEN_UPLOAD_MODAL = 'REOPEN_UPLOAD_MODAL',
  VERIFICATION__SUCCESS = 'VERIFICATION__SUCCESS',
  VERIFICATION__FAILURE = 'VERIFICATION__FAILURE',
  VERIFICATION_POLLING__SUCCESS = 'VERIFICATION_POLLING__SUCCESS',
  VERIFICATION_POLLING__FAILURE = 'VERIFICATION_POLLING__FAILURE',
  CREATE_OR_UPDATE_EDITION__STARTED = 'CREATE_OR_UPDATE_EDITION__STARTED',
  CREATE_OR_UPDATE_EDITION__SUCCESS = 'CREATE_OR_UPDATE_EDITION__SUCCESS',
  CREATE_OR_UPDATE_EDITION__FAILURE = 'CREATE_OR_UPDATE_EDITION__FAILURE',
  CREATE_OR_UPDATE_EDITION_POLLING__SUCCESS = 'CREATE_OR_UPDATE_EDITION_POLLING__SUCCESS',
  RESET_FILE_DATA = 'RESET_FILE_DATA',
  READ_LOCAL_FILE_FOR_PREVIEW = 'READ_LOCAL_FILE_FOR_PREVIEW',
  UPLOAD_PREVIEW__STARTED = 'UPLOAD_PREVIEW__STARTED',
  PREVIEW_POLLING__SUCCESS = 'PREVIEW_POLLING__SUCCESS',
  PREVIEW_VERIFICATION__SUCCESS = 'PREVIEW_VERIFICATION__SUCCESS',
  DELETE_UPLOADED_PREVIEWS__STARTED = 'DELETE_UPLOADED_PREVIEWS__STARTED',
  DELETE_UPLOADED_PREVIEWS__SUCCESS = 'DELETE_UPLOADED_PREVIEWS__SUCCESS',
  DELETE_UPLOADED_PREVIEWS__FAILURE = 'DELETE_UPLOADED_PREVIEWS__FAILURE',
}

export interface IReadLocalFileAction {
  type: types.READ_LOCAL_FILE
  modalId: string
  fileInstance: File | null
  groupId: string
  existingEdition?: IEditionMetadata
}

export interface IReadLocalFileValidationFailureAction {
  type: types.READ_LOCAL_FILE__VALIDATE_FAILURE
  messages: string[] | undefined
}

export interface IReadLocalFileCancelAction {
  type: types.READ_LOCAL_FILE__CANCEL
  message: string
}

export interface ICloseUploadDialogAction {
  type: types.CLOSE_UPLOAD_DIALOG
}

export interface IUploadFileAction {
  type: types.UPLOAD__STARTED
  modalId: string
  name: string
  groupId: string
  editionsToUpdate: string[]
  onProgress: OnProgressCallbackType
}

export interface IRetrievedTmpLinkAction {
  type: types.UPLOAD__RETRIEVED_TMP_LINK
  id: string
}

export interface ISetProgressAction {
  type: types.UPLOAD__PROGRESS
  progress: number
}

export interface IFailureAction {
  type:
    | types.UPLOAD__FAILURE
    | types.CREATE_OR_UPDATE_EDITION__FAILURE
    | types.VERIFICATION__FAILURE
    | types.DELETE_UPLOADED_PREVIEWS__FAILURE
  message: string
  correlationId: string
  disableUploadAndShowCloseButton?: boolean
}

export interface IUploadSuccessAction {
  type: types.UPLOAD__SUCCESS
}

export interface IUploadCanceledAction {
  type: types.UPLOAD__CANCELED
  matchedEdition: IEditionMetadata
}

export interface IReopenUploadModal {
  type: types.REOPEN_UPLOAD_MODAL
  modalId: string
}

export interface IVerificationSuccessAction {
  type: types.VERIFICATION__SUCCESS
}

export interface IVerificationPollingSuccessAction {
  type: types.VERIFICATION_POLLING__SUCCESS
  groupId: string
  data: IPollingTaskData
}

export interface IVerificationPollingFailureAction {
  type: types.VERIFICATION_POLLING__FAILURE
  message: string
  correlationId: string
}

export interface ICreateOrUpdateEditionAction {
  type: types.CREATE_OR_UPDATE_EDITION__STARTED
  fileId: string
}

export interface ICreateOrUpdateEditionSuccessAction {
  type: types.CREATE_OR_UPDATE_EDITION__SUCCESS
  message: string
}

export interface ICreateOrUpdateEditionPollingSuccess {
  type: types.CREATE_OR_UPDATE_EDITION_POLLING__SUCCESS
  data: IPollingTaskData
}

export interface IResetFileDataAction {
  type: types.RESET_FILE_DATA
}

export interface IReadLocalFileForPreviewAction {
  type: types.READ_LOCAL_FILE_FOR_PREVIEW
  modalId: string
  fileInstance: File | null
}

export interface IUploadPreviewAction {
  type: types.UPLOAD_PREVIEW__STARTED
  onProgress: OnProgressCallbackType
  setPreviewField: SetPreviewFieldType
  modalId: string
  fileName: string
}

export interface IPreviewPollingSuccessAction {
  type: types.PREVIEW_POLLING__SUCCESS
  setPreviewField: SetPreviewFieldType
}

export interface IPreviewVerificationSuccessAction {
  type: types.PREVIEW_VERIFICATION__SUCCESS
}

export interface IDeleteUploadedPreviewsAction {
  type: types.DELETE_UPLOADED_PREVIEWS__STARTED
  values: PackType
}

export interface IDeleteUploadedPreiewsSuccessAction {
  type: types.DELETE_UPLOADED_PREVIEWS__SUCCESS
}

export type FileActionsType =
  | IReadLocalFileAction
  | IReadLocalFileValidationFailureAction
  | IReadLocalFileCancelAction
  | ICloseUploadDialogAction
  | IUploadFileAction
  | IRetrievedTmpLinkAction
  | ISetProgressAction
  | IFailureAction
  | IUploadSuccessAction
  | IUploadCanceledAction
  | IReopenUploadModal
  | IVerificationSuccessAction
  | ICreateOrUpdateEditionAction
  | ICreateOrUpdateEditionSuccessAction
  | IResetFileDataAction
  | IReadLocalFileForPreviewAction
  | IUploadPreviewAction
  | IPreviewPollingSuccessAction
  | IPreviewVerificationSuccessAction
  | IDeleteUploadedPreiewsSuccessAction
  | IDeleteUploadedPreiewsSuccessAction
  | IInitAction

// Reducer types
export interface IFile {
  openedModal?: string
  name: string
  extension: string
  size: string
  mimetype: string
  progress: number
  uploadURI?: string
  fileInstance?: File
  id?: string
  uploadError?: string
  correlationId?: string
  fileErrors?: string[]
  disableUploadAndShowCloseButton?: boolean
  existingEdition?: IEditionMetadata
  editionsToUpdate: string[]
}

// Miscellaneous
export type SetPreviewFieldType = (fileId: string) => void

export type SetPreviewFieldWrapperType = (
  newTmpPreview: string
) => SetPreviewFieldType

export type OnProgressCallbackType = (progress: number) => void

export interface ICreateTmpLinkResponse {
  uploadURI: string
  id: string
}
